import { PositioningMethod } from "building-types/annotation/annotationTypesEnum";
import type { Doc } from "building-types/doc";
import type {
  DocEntityHandle,
  RoomEdgeHandle,
  RoomHandle,
  RoomLayoutHandle,
  RoomLayoutInstanceHandle,
} from "building-types/handles/docEntityHandleTypes";
import { EntityInstanceType } from "building-types/roomLayouts/roomLayoutEligibleDocEntity";
import type {
  EntityInstance,
  RoomLayout,
  RoomLayoutDocEntity,
  RoomLayoutEligibleEntity,
  RoomLayoutEntity,
  RoomLayoutEntityHandle,
  RoomLayoutEntityType,
  RoomLayoutInstance,
} from "building-types/roomLayouts/roomLayoutTypes";
import { handleAuxiliaryField, roomLayoutEntityTypes } from "building-types/roomLayouts/roomLayoutTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { StaticRoom } from "building-types/rooms/staticRoomTypes";
import type { RWTable, Table } from "common/data/table";
import { docEntityIsInRoomLayout } from "../../docRoomLayouts";
import { docEntityHandleHash } from "../../entity/core/handleHash";
import { docEntityGetSortedChildrenFromTable } from "../../entity/docEntityGetChildrenFromTable";
import docEntityGetParent from "../../entity/docEntityGetParent";
import { docEntityTableFilter } from "../../entity/docEntityTableFilter";
import { roomLayoutEligibleEntityTypes } from "./roomLayoutEligibleEntityTypes";

export function isRoomLayoutEligibleEntity(entity: any): entity is RoomLayoutDocEntity {
  return "entityType" in entity && roomLayoutEligibleEntityTypes[entity.entityType];
}

export function isRoomLayoutEligible(doc: Doc, roomLayoutEntity: RoomLayoutDocEntity) {
  if (!isRoomLayoutEligibleEntity(roomLayoutEntity)) {
    return false;
  }

  const parent = docEntityGetParent(doc, { type: roomLayoutEntity.entityType, id: roomLayoutEntity.id });

  if (!parent) {
    return false;
  }

  switch (roomLayoutEntity.entityType) {
    case "ANNOTATION":
      if (parent.type === "ELEVATION_VIEW") {
        return false;
      }

      if (roomLayoutEntity.positioningMethod === PositioningMethod.GLOBAL) {
        return false;
      }
      return true;
    case "CONTENT_TEMPLATE_INSTANCE":
      if (
        parent.type === "LEVEL" ||
        parent.type === "ELEVATION_VIEW" ||
        parent.type === "SECTION_VIEW" ||
        parent.type === "CONTENT_TEMPLATE" ||
        parent.type === "CONTENT_TEMPLATE_INSTANCE"
      ) {
        return false;
      }

      if (roomLayoutEntity.templateType !== "DRAWING") {
        return false;
      }

      if (roomLayoutEntity.positioningMethod === PositioningMethod.GLOBAL) {
        return false;
      }
      return true;
    case "CASEWORK":
    case "PROP_INSTANCE":
    case "OPENING":
      return true;
  }
}

export interface EntityInstancesForEntityOptions {
  readonly hasRoomLayouts: boolean;
  readonly docEntityRoomLayouts: Table<string>;
  readonly docRoomLayouts: Table<RoomLayout>;
  readonly rooms: Table<StaticRoom> | Table<Room>;
  readonly activeRoomLayoutInstances: Table<RoomLayoutInstance>;
  readonly roomLayoutInstancesByRoomLayoutId?: Table<Table<RoomLayoutInstance>>;
  readonly entity: RoomLayoutEligibleEntity;
}

/** Pre-index room layout instances by roomLayoutId to avoid scanning all instances per entity. */
export function buildRoomLayoutInstancesByRoomLayoutId(
  activeRoomLayoutInstances: Table<RoomLayoutInstance>,
): Table<Table<RoomLayoutInstance>> {
  const byRoomLayoutId: RWTable<RWTable<RoomLayoutInstance>> = {};
  for (const roomLayoutInstanceId in activeRoomLayoutInstances) {
    const roomLayoutInstance = activeRoomLayoutInstances[roomLayoutInstanceId];
    const { roomLayoutId } = roomLayoutInstance;
    if (!byRoomLayoutId[roomLayoutId]) {
      byRoomLayoutId[roomLayoutId] = {};
    }
    byRoomLayoutId[roomLayoutId][roomLayoutInstanceId] = roomLayoutInstance;
  }
  return byRoomLayoutId;
}

/**
 * Returns the entity instances for a given entity. If an entity participates in a room layout,
 * it could have multiple instances.
 */
export function entityInstancesForEntity({
  hasRoomLayouts,
  docEntityRoomLayouts,
  docRoomLayouts,
  activeRoomLayoutInstances,
  roomLayoutInstancesByRoomLayoutId,
  rooms,
  entity,
}: EntityInstancesForEntityOptions) {
  const roomLayoutInstances: EntityInstance[] = [];

  // quick exit for performance, if there aren't any room layouts in the doc
  if (!hasRoomLayouts) {
    // this is the default entity that would be generated if there were no room layouts
    roomLayoutInstances.push({
      parentId: entity.parentId,
      edgeId: !!entity.edgeIds ? undefined : entity.edgeId,
      edgeIds: !!entity.edgeIds ? entity.edgeIds : undefined,
      entityInstanceType: undefined,
      mirrored: false,
    });
    return roomLayoutInstances;
  }

  const hash = docEntityHandleHash(roomLayoutEntityHandleFromTypeAndId(entity.entityType, entity.id)); // make generic for any entity type
  const roomLayoutId = docEntityRoomLayouts[hash];

  if (!roomLayoutId) {
    // this is the default entity that would be generated if if no room layout was applied to this entity
    roomLayoutInstances.push({
      parentId: entity.parentId,
      edgeId: entity.edgeId,
      entityInstanceType: undefined,
      mirrored: false,
    });
  } else {
    const roomLayout = docRoomLayouts[roomLayoutId];

    // a room layout can be deleted or doc-referenced room layout can be removed
    if (!roomLayout) {
      return roomLayoutInstances;
    }
    const roomLayoutSourceRoomId = roomLayout?.parentId;

    // this is the entity generated in the source room layout room
    roomLayoutInstances.push({
      parentId: roomLayoutSourceRoomId,
      edgeId: !!entity.edgeIds ? undefined : entity.edgeId,
      edgeIds: !!entity.edgeIds ? entity.edgeIds : undefined,
      entityInstanceType: EntityInstanceType.ROOM_LAYOUT_SOURCE,
      mirrored: false,
    });

    const roomLayoutInstanceTable =
      roomLayoutInstancesByRoomLayoutId?.[roomLayoutId] ??
      docEntityTableFilter(activeRoomLayoutInstances, (x) => x.roomLayoutId === roomLayoutId).newTable;

    // this loop creates the entities generated in the room layout instance rooms
    for (const roomLayoutInstanceId in roomLayoutInstanceTable) {
      const roomLayoutInstance = roomLayoutInstanceTable[roomLayoutInstanceId];
      const roomLayoutInstanceSourceRoomId = roomLayoutInstance.parentId;
      const { edgeIdRemapping, suppressedEntities } = roomLayoutInstance;

      // if the entity is suppressed per the room layout instance, it won't be generated
      if (!!suppressedEntities && suppressedEntities[entity.id]) {
        continue;
      }

      // flips value to key and then makes value into array of values including undefined as a valid value
      const invertedRemapping = simplifyEdgeIdRemapping(edgeIdRemapping, rooms[roomLayoutInstance.parentId]);

      // remaps can map an edge id to multiple edges ids
      const remapEdgeItems = remapRoomLayoutEntityEdges(entity, invertedRemapping);

      for (const { edgeId, edgeIds } of remapEdgeItems ?? []) {
        const room = rooms[roomLayoutInstanceSourceRoomId];

        // these are the entities generated in the room layout instance rooms
        roomLayoutInstances.push({
          parentId: roomLayoutInstanceSourceRoomId,
          edgeId: !!entity.edgeIds ? undefined : edgeId,
          edgeIds: !!entity.edgeIds ? edgeIds?.filter((x) => room?.edgeIds?.includes(x)) : undefined,
          entityInstanceType: EntityInstanceType.ROOM_LAYOUT_INSTANCE,
          handleOverride: { type: "ROOM_LAYOUT_INSTANCE", id: roomLayoutInstanceId },
          mirrored: false,
        });
      }
    }
  }

  return roomLayoutInstances;
}

export function entityHandlesIfRoomLayoutInstanceOverride<T extends RoomLayoutEntityHandle>(
  entityHandle: T,
  handleOverride?: RoomLayoutInstanceHandle,
) {
  const handle: RoomLayoutInstanceHandle | T = handleOverride ?? entityHandle;
  const handleAuxiliary: T | undefined = handleOverride ? entityHandle : undefined;
  return { handleAuxiliary, handle };
}

interface RoomLike {
  edgeIds?: string[];
}

export function simplifyEdgeIdRemapping(
  edgeIdRemapping: { [instanceEdgeId: string]: string | null } = {},
  room: RoomLike,
) {
  const defaultAssignedEdges = room?.edgeIds?.filter((x) => !Object.keys(edgeIdRemapping).includes(x)) ?? [];
  const invertedRemapping: { [sourceLayoutEdgeId: string]: string[] } = {};

  for (const instanceEdgeId in edgeIdRemapping) {
    const sourceLayoutEdgeId = edgeIdRemapping[instanceEdgeId];
    if (sourceLayoutEdgeId === null) {
      continue;
    }

    const instanceEdges = invertedRemapping[sourceLayoutEdgeId] ?? [];
    instanceEdges.push(instanceEdgeId);
    invertedRemapping[sourceLayoutEdgeId] = instanceEdges;
  }

  for (const defaultAssignedEdge of defaultAssignedEdges) {
    const instanceEdges = invertedRemapping[defaultAssignedEdge] ?? [];
    instanceEdges.push(defaultAssignedEdge);
    invertedRemapping[defaultAssignedEdge] = instanceEdges;
  }

  return invertedRemapping;
}

interface RemapEdgeItem {
  readonly edgeId?: string | null;
  readonly edgeIds?: string[] | null;
}

export function remapRoomLayoutEntityEdges(
  entity: RoomLayoutEligibleEntity,
  sourceToInstanceEdgeMapping: { [sourceLayoutEdgeId: string]: string[] },
) {
  if (!entity.edgeIds) {
    if (!sourceToInstanceEdgeMapping) {
      // if no mapping for any edge, just use the original edge id from the layout
      return [{ edgeId: entity.edgeId }];
    } else {
      const remapEdgeItems: RemapEdgeItem[] = [];
      const results = entity.edgeId ? sourceToInstanceEdgeMapping[entity.edgeId] : undefined;

      // if there's no array of results, just use the original edge id from the layout
      if (!results) {
        return [];
      }

      // otherwise create a remap edge item for each mapped edge or null edge (null effectively mutes entities)
      for (const result of results) {
        remapEdgeItems.push({ edgeId: result });
      }

      return remapEdgeItems;
    }
  }

  /**
   * we need to remap the edge ids if the entity has multiple edge ids (currently only {@link WallCaseworkDynamicDeprecated} uses this code path)
   *  */
  if (!!entity.edgeIds) {
    if (!sourceToInstanceEdgeMapping) {
      // if no mapping for any edge, just use the original edge ids from the layout
      return [{ edgeIds: entity.edgeIds }];
    } else {
      const newEdgeIds: { [edgeId: string]: true } = {};
      for (const edgeId of entity.edgeIds) {
        const results = sourceToInstanceEdgeMapping[edgeId];
        // if there's no array of results, just use the original edge id from the layout
        if (results) {
          // otherwise create a remap edge item for each mapped edge or ignore null
          for (const result of results) {
            newEdgeIds[result] = true;
          }
        }
      }
      const newEdgeKeys = Object.keys(newEdgeIds);
      // if all edges have been muted, then send an empty array
      if (!newEdgeKeys.length) {
        return [];
      }
      // otherwise embed all the new edges into a single object
      return [{ edgeIds: newEdgeKeys }];
    }
  }
  return undefined;
}

/**
 * Version of {@link docGetEntityHandleById} for {@link RoomLayoutEntityType} that doesn't have to loop through all types.
 */
export function roomLayoutEntityHandleFromTypeAndId(type: RoomLayoutEntityType, id: string): RoomLayoutEntityHandle {
  switch (type) {
    case "PROP_INSTANCE": {
      return { type: "PROP_INSTANCE", id };
    }
    case "CASEWORK": {
      return { type: "CASEWORK", id };
    }
    case "OPENING": {
      return { type: "OPENING", id };
    }
    case "ANNOTATION": {
      return { type: "ANNOTATION", id };
    }
    case "CONTENT_TEMPLATE_INSTANCE": {
      return { type: "CONTENT_TEMPLATE_INSTANCE", id };
    }
  }
}

export type RoomLayoutRelatedHandle = RoomHandle | RoomLayoutHandle | RoomLayoutInstanceHandle | RoomEdgeHandle;

export function isRoomLayoutRelatedHandle(handle: DocEntityHandle): handle is RoomLayoutRelatedHandle {
  return (
    handle.type === "ROOM_LAYOUT" ||
    handle.type === "ROOM" ||
    (handle.type === "EDGE" && handle.parent.type === "ROOM") ||
    handle.type === "ROOM_LAYOUT_INSTANCE"
  );
}

export function getRoomHandleAndEdgeIdFromHandle(
  handle: RoomLayoutRelatedHandle,
  docRooms: Table<StaticRoom>,
  docRoomLayouts: Table<RoomLayout>,
  docRoomLayoutInstances: Table<RoomLayoutInstance>,
): { roomHandle: RoomHandle; edgeId: string | undefined } {
  switch (handle.type) {
    case "ROOM": {
      return { roomHandle: { ...handle }, edgeId: getEdgeIdFromRoom(docRooms, handle.id) };
    }
    case "EDGE": {
      const id = handle.parent.id;
      return { roomHandle: { type: "ROOM", id }, edgeId: getEdgeIdFromRoom(docRooms, id, handle.edgeId) };
    }
    case "ROOM_LAYOUT": {
      const id = docRoomLayouts[handle.id]?.parentId;
      return { roomHandle: { type: "ROOM", id }, edgeId: getEdgeIdFromRoom(docRooms, id) };
    }
    case "ROOM_LAYOUT_INSTANCE": {
      const id = docRoomLayoutInstances[handle.id]?.parentId;
      return { roomHandle: { type: "ROOM", id }, edgeId: getEdgeIdFromRoom(docRooms, id) };
    }
  }
}

function getEdgeIdFromRoom(docRooms: Table<StaticRoom>, id: string | undefined, edgeId: number = 0) {
  if (!id) {
    return undefined;
  }

  const room = docRooms[id];
  if (!room) {
    return undefined;
  }
  return room.edgeIds?.length ? room.edgeIds[edgeId] : undefined;
}

export function getRoomHandleFromHandle(
  handle: RoomHandle | RoomLayoutHandle | RoomEdgeHandle,
  docRoomLayouts: Table<RoomLayout>,
): RoomHandle {
  switch (handle.type) {
    case "ROOM":
      return { ...handle };
    case "ROOM_LAYOUT":
      return { type: "ROOM", id: docRoomLayouts[handle.id]?.parentId };
    case "EDGE":
      return { type: "ROOM", id: handle.parent.id };
  }
}

export interface DeriveEntityTableFromInspectorHandleProps<T extends RoomLayoutEligibleEntity> {
  readonly docRoomLayouts: Table<RoomLayout>;
  readonly docRoomLayoutInstances: Table<RoomLayoutInstance>;
  readonly docEntities: Table<T>;
  readonly handle: RoomLayoutRelatedHandle;
  readonly entityRoomLayouts: Table<string>;
  /** Only required if handle is a RoomEdgeHandle */
  readonly docRooms?: Table<StaticRoom>;
}

export function deriveEntityTableFromInspectorHandle<T extends RoomLayoutEligibleEntity>({
  handle,
  docEntities,
  docRoomLayouts,
  docRoomLayoutInstances,
  entityRoomLayouts,
  docRooms = {},
}: DeriveEntityTableFromInspectorHandleProps<T>) {
  const entityTable: RWTable<T> = {};
  if (!handle) {
    return entityTable;
  }
  for (const entityId in docEntities) {
    const entity = docEntities[entityId];

    const entityHash = docEntityHandleHash(roomLayoutEntityHandleFromTypeAndId(entity.entityType, entityId));
    if (handle.type === "ROOM") {
      const roomId = handle.id;
      if (entity.parentId !== roomId) {
        if (
          !roomLayoutOrInstanceHandle({
            docRoomLayoutInstances,
            roomId,
            entityRoomLayouts,
            entityHash,
            entityId,
            docRoomLayouts,
          })
        ) {
          continue;
        }
      }
    } else if (handle.type === "EDGE") {
      const room = docRooms[handle.parent.id];
      if (entity.parentId !== handle.parent.id || entity.edgeId !== room?.edgeIds?.[handle?.edgeId]) {
        continue;
      }
    } else if (handle.type === "ROOM_LAYOUT") {
      const roomLayout = docRoomLayouts[handle.id];
      if (entity.parentId !== roomLayout.parentId) {
        continue;
      }

      if (!entityRoomLayouts[entityHash] || entityRoomLayouts[entityHash] !== handle.id) {
        continue;
      }
    } else if (handle.type === "ROOM_LAYOUT_INSTANCE") {
      const roomLayoutInstance = docRoomLayoutInstances[handle.id];
      const roomLayout = docRoomLayouts[roomLayoutInstance.roomLayoutId];
      if (entity.parentId !== roomLayout?.parentId) {
        continue;
      }

      if (!entityRoomLayouts[entityHash] || entityRoomLayouts[entityHash] !== roomLayout.id) {
        continue;
      }
    }

    entityTable[entityId] = entity;
  }
  return entityTable;
}

export function roomLayoutOrInstanceHandle({
  roomId,
  docRoomLayoutInstances,
  entityRoomLayouts,
  entityHash,
  docRoomLayouts,
}: {
  docRoomLayoutInstances: Table<RoomLayoutInstance>;
  roomId: string;
  entityRoomLayouts: Table<string>;
  entityHash: string;
  entityId: string;
  docRoomLayouts: Table<RoomLayout>;
}): RoomLayoutHandle | RoomLayoutInstanceHandle | undefined {
  const roomLayoutInstances = docEntityGetSortedChildrenFromTable(docRoomLayoutInstances, roomId);
  for (const roomLayoutInstance of roomLayoutInstances) {
    const { roomLayoutId } = roomLayoutInstance;
    if (entityRoomLayouts[entityHash] === roomLayoutId) {
      return { type: "ROOM_LAYOUT_INSTANCE", id: roomLayoutInstance.id };
    }
  }

  const roomLayouts = docEntityGetSortedChildrenFromTable(docRoomLayouts, roomId);
  for (const roomLayout of roomLayouts) {
    if (entityRoomLayouts[entityHash] === roomLayout.id) {
      return { type: "ROOM_LAYOUT", id: roomLayout.id };
    }
  }

  return undefined;
}

export function deriveEdgeIdFromHandle(handle: RoomLayoutRelatedHandle, doc: Doc) {
  switch (handle.type) {
    case "ROOM": {
      return doc.rooms[handle.id]?.edgeIds?.[0] ?? undefined;
    }
    case "ROOM_LAYOUT": {
      const roomId = doc.roomLayouts[handle.id]?.parentId;
      if (!roomId) {
        return undefined;
      }
      return doc.rooms[roomId]?.edgeIds?.[0] ?? undefined;
    }
    case "EDGE":
    case "ROOM_LAYOUT_INSTANCE": {
      // can't add new edge-based entities to room layout instances
      return undefined;
    }
  }
}

export function getRoomLayoutInstanceId(
  doc: Doc,
  handle: DocEntityHandle,
  entityListContextHandle: RoomLayoutRelatedHandle,
) {
  if (!entityListContextHandle || !docEntityIsInRoomLayout(doc, handle)) {
    return undefined;
  }

  const { roomHandle } = getRoomHandleAndEdgeIdFromHandle(
    entityListContextHandle,
    doc.rooms,
    doc.roomLayouts,
    doc.roomLayoutInstances,
  );
  if (!roomHandle) {
    return undefined;
  }

  const entityHash = docEntityHandleHash(handle as RoomLayoutEntityHandle);
  const result = roomLayoutOrInstanceHandle({
    docRoomLayouts: doc.roomLayouts,
    docRoomLayoutInstances: doc.roomLayoutInstances,
    roomId: roomHandle.id,
    entityRoomLayouts: doc.entityRoomLayouts,
    entityId: (handle as RoomLayoutEntityHandle).id,
    entityHash,
  });
  if (!result || result.type !== "ROOM_LAYOUT_INSTANCE") {
    return undefined;
  }
  return result.id;
}

export function isRoomLayoutInstanceHandle(handle: DocEntityHandle): handle is RoomLayoutInstanceHandle {
  return handle?.type === "ROOM_LAYOUT_INSTANCE";
}

export function getDocEntityIdFromGeneratedRoomLayoutEligibleEntity(
  entity: RoomLayoutEntity | undefined,
): string | undefined {
  if (!entity) {
    return undefined;
  }

  if (entity[handleAuxiliaryField]) {
    return entity[handleAuxiliaryField].id;
  }

  if ("handle" in entity) {
    return entity.handle?.id;
  }

  return entity.id;
}

export function isRoomLayoutEntityHandle(entity: DocEntityHandle): entity is RoomLayoutEntityHandle {
  return roomLayoutEntityTypes.some((type) => entity?.type === type);
}
